import React from 'react';
import { useTranslation } from 'react-i18next';
import WorkbenchPreviewImage from '@/components/ipe/WorkbenchPreviewImage';
import '@/styles/ipe/workbench/modals/DetailToolBridgeSourcesPreview.css';

const PREVIEW_LIMIT = 24;

function resolveSourceLabel(item, index, t) {
  return String(
    item?.title
    || item?.bridgeListingGroupTitle
    || item?.name
    || '',
  ).trim() || t('detailToolBridgeSources.imageFallback', { index: index + 1 });
}

/**
 * 快捷功能确认弹窗：展示将带入的主图、详情图与标题。
 */
export default function DetailToolBridgeSourcesPreview({
  sources = [],
  className = '',
  title = '',
}) {
  const { t } = useTranslation('workbench');
  const list = [];
  (Array.isArray(sources) ? sources : []).forEach((item, index) => {
    const url = String(item?.url || '').trim();
    const preview = String(
      item?.previewUrl || item?.viewUrl || item?.displayUrl || url,
    ).trim();
    const label = resolveSourceLabel(item, index, t);
    const role = String(item?.listingRole || '').trim().toLowerCase();
    const printCount = Array.isArray(item?.printImageUrls)
      ? item.printImageUrls.filter(Boolean).length
      : 0;
    const detailUrls = Array.isArray(item?.detailImageUrls)
      ? item.detailImageUrls.map((u) => String(u || '').trim()).filter(Boolean)
      : [];

    if (url || preview) {
      const badge = role === 'detail' ? t('detailToolBridgeSources.badgeDetail') : t('detailToolBridgeSources.badgeMain');
      list.push({
        key: `${item?.id || preview || url || index}:main`,
        url: url || preview,
        preview: preview || url,
        label: printCount > 0 ? `${label}${t('detailToolBridgeSources.printSuffix', { count: printCount })}` : label,
        badge,
      });
    }

    detailUrls.forEach((detailUrl, detailIndex) => {
      if (role === 'detail' && (detailUrl === url || detailUrl === preview)) return;
      list.push({
        key: `${item?.id || index}:detail:${detailIndex}`,
        url: detailUrl,
        preview: detailUrl,
        label,
        badge: t('detailToolBridgeSources.badgeDetail'),
      });
    });
  });

  if (list.length === 0) return null;

  const visible = list.slice(0, PREVIEW_LIMIT);
  const overflow = list.length - visible.length;

  return (
    <section
      className={`detail-tool-bridge-sources${className ? ` ${className}` : ''}`}
      aria-label={title || t('detailToolBridgeSources.title')}
    >
      <div className="detail-tool-bridge-sources__head">
        <h3 className="detail-tool-bridge-sources__title">{title || t('detailToolBridgeSources.title')}</h3>
        <span className="detail-tool-bridge-sources__count">{t('detailToolBridgeSources.itemCount', { count: list.length })}</span>
      </div>
      <ul className="detail-tool-bridge-sources__grid">
        {visible.map((item) => (
          <li key={item.key} className="detail-tool-bridge-sources__card">
            <div className="detail-tool-bridge-sources__thumb" title={item.badge}>
              <WorkbenchPreviewImage
                url={item.url}
                displayUrl={item.preview}
                alt={item.label}
                loading="lazy"
              />
              <span className="detail-tool-bridge-sources__badge">{item.badge}</span>
            </div>
            <p className="detail-tool-bridge-sources__name" title={item.label}>
              {item.label}
            </p>
          </li>
        ))}
        {overflow > 0 ? (
          <li className="detail-tool-bridge-sources__card detail-tool-bridge-sources__card--more">
            <div className="detail-tool-bridge-sources__thumb detail-tool-bridge-sources__thumb--more">
              +{overflow}
            </div>
            <p className="detail-tool-bridge-sources__name">{t('detailToolBridgeSources.moreHidden')}</p>
          </li>
        ) : null}
      </ul>
    </section>
  );
}
